// AppError hierarchy — typed errors for the entire application

export type ErrorSeverity = 'critical' | 'warning' | 'info';

export type ErrorCode =
  | 'VALIDATION_ERROR'
  | 'AUTH_ERROR'
  | 'AUTH_EXPIRED'
  | 'NETWORK_ERROR'
  | 'NETWORK_OFFLINE'
  | 'ENTITY_SCOPE_ERROR'
  | 'COMPLIANCE_ERROR'
  | 'AGENT_ERROR'
  | 'AGENT_TIMEOUT'
  | 'NOT_FOUND'
  | 'FORBIDDEN'
  | 'RATE_LIMITED'
  | 'UPGRADE_REQUIRED'
  | 'UNKNOWN';

export class AppError extends Error {
  readonly code: ErrorCode;
  readonly severity: ErrorSeverity;
  readonly recoverable: boolean;
  readonly context?: Record<string, unknown>;
  readonly timestamp: string;

  constructor(params: {
    code: ErrorCode;
    message: string;
    severity?: ErrorSeverity;
    recoverable?: boolean;
    context?: Record<string, unknown>;
    cause?: Error;
  }) {
    super(params.message);
    this.name = 'AppError';
    this.code = params.code;
    this.severity = params.severity ?? 'warning';
    this.recoverable = params.recoverable ?? true;
    this.context = params.context;
    this.timestamp = new Date().toISOString();
    if (params.cause) this.cause = params.cause;
  }

  toAuditPayload() {
    return {
      code: this.code,
      message: this.message,
      severity: this.severity,
      context: this.context,
      timestamp: this.timestamp,
    };
  }
}

export class ValidationError extends AppError {
  readonly fieldErrors: Record<string, string>;

  constructor(
    message: string,
    fieldErrors: Record<string, string>,
    context?: Record<string, unknown>,
  ) {
    super({
      code: 'VALIDATION_ERROR',
      message,
      severity: 'warning',
      recoverable: true,
      context,
    });
    this.name = 'ValidationError';
    this.fieldErrors = fieldErrors;
  }
}

export class AuthError extends AppError {
  constructor(message: string, expired = false) {
    super({
      code: expired ? 'AUTH_EXPIRED' : 'AUTH_ERROR',
      message,
      severity: 'critical',
      recoverable: expired,
    });
    this.name = 'AuthError';
  }
}

export class NetworkError extends AppError {
  constructor(message: string, offline = false) {
    super({
      code: offline ? 'NETWORK_OFFLINE' : 'NETWORK_ERROR',
      message,
      severity: offline ? 'warning' : 'critical',
      recoverable: true,
    });
    this.name = 'NetworkError';
  }
}

export class EntityScopeError extends AppError {
  constructor(message = 'You do not have access to this entity') {
    super({
      code: 'ENTITY_SCOPE_ERROR',
      message,
      severity: 'critical',
      recoverable: false,
    });
    this.name = 'EntityScopeError';
  }
}

export class AgentError extends AppError {
  readonly agentName: string;

  constructor(agentName: string, message: string, timeout = false) {
    super({
      code: timeout ? 'AGENT_TIMEOUT' : 'AGENT_ERROR',
      message,
      severity: 'warning',
      recoverable: true,
      context: { agentName },
    });
    this.name = 'AgentError';
    this.agentName = agentName;
  }
}

/**
 * Server error body shape emitted by the Lambda translateError module.
 * Everything is optional because older handlers may still return bare strings.
 */
interface ServerErrorBody {
  error?: string;        // EITHER a machine-readable code ('MissingRequiredField', 'InvalidFieldValue', ...
                          // — the legacy translateError shape, always paired with `detail`/`field`/`hint`
                          // for the actual message) OR, when `code` below is also present, a deliberately-
                          // worded, already-safe-to-show human sentence (the newer convention — see
                          // decidePTORequest's SELF_APPROVAL_FORBIDDEN / REQUEST_NOT_SUBMITTED responses).
  code?: string;          // machine-readable code in the NEWER convention — its presence is the signal
                          // that `error` above is a human sentence, not a machine code (see friendlyMessage).
  field?: string;        // column name on NOT NULL violation
  constraint?: string;   // pg constraint name on CHECK / UNIQUE / FK
  detail?: string;       // human-readable detail line (usually pg's)
  hint?: string;         // guidance for the user
  requestId?: string;    // API Gateway request id for support
  errorClass?: string;   // JS class name on unknown server-side error
  currentStatus?: string; // e.g. attest/delete conflicts
  requiredTier?: string; // B1 tier-gate 402 — internal tier key needed (e.g. 'professional')
}

function parseServerError(body?: string): ServerErrorBody | null {
  if (!body) return null;
  try {
    const parsed = JSON.parse(body);
    return typeof parsed === 'object' && parsed !== null ? (parsed as ServerErrorBody) : null;
  } catch {
    return null;
  }
}

/** Human-friendly message from a structured server error. Falls back to
 *  the raw body when the server didn't return our shape. */
function friendlyMessage(parsed: ServerErrorBody | null, rawBody: string | undefined, fallback: string): string {
  if (!parsed) return rawBody || fallback;
  // Comprehensive-sweep audit fix: some newer handlers (decidePTORequest's
  // segregation-of-duties + stale-status checks) return {error: '<already
  // safe, human-readable sentence>', code: 'MACHINE_CODE'} instead of the
  // legacy {error: 'MachineCode', detail: '<message>'} shape the switch
  // below expects. Without this, those carefully-worded messages never
  // matched a case and fell through to a generic fallback that discarded
  // the actual guidance (e.g. "You cannot approve your own time-off
  // request... Another admin or owner must approve it." became a plain
  // "You do not have permission for this action."). `code`'s presence is
  // the reliable signal: only handlers written for THIS convention set it,
  // so trusting `error` as display text here can't accidentally surface a
  // legacy machine-code string (which never carries `code`) or leak raw
  // technical detail (W2-31's concern was specifically about `rawBody`,
  // not a field a handler deliberately populated for display).
  if (parsed.code && parsed.error) return parsed.error;
  switch (parsed.error) {
    case 'MissingRequiredField':
      return parsed.field
        ? `Missing required field: ${parsed.field}`
        : (parsed.detail || 'A required field is missing.');
    case 'InvalidFieldValue':
      return parsed.hint
        ? `Invalid value: ${parsed.hint}`
        : (parsed.detail || 'One of the submitted values is not allowed.');
    case 'DuplicateValue':
      return parsed.detail || 'That value is already in use.';
    case 'ReferencedRecordMissing':
      return parsed.detail || 'A referenced record could not be found.';
    case 'InvalidInput':
      return parsed.detail || 'The submitted input is malformed.';
    case 'InvalidJSON':
      return 'The request body is not valid JSON.';
    case 'Forbidden':
      return parsed.detail || 'You do not have permission for this action.';
    case 'ConcurrentUpdate':
      return 'This record was updated by someone else. Refresh and try again.';
    case 'NetworkFailure':
      return parsed.detail || 'Your device could not reach the server.';
    case 'InternalError':
      return parsed.hint
        ? `The server hit an unexpected error. ${parsed.hint}`
        : 'The server hit an unexpected error. Please retry.';
    default:
      // W2-31 (2026-06-09): do NOT fall back to rawBody — it leaks raw server
      // payloads to users (e.g. `{"error":"Unknown route: /me/..."}` surfaced
      // verbatim in the Notifications ErrorBanner). Show the structured detail
      // if present, else the generic fallback. requestId is still preserved on
      // the AppError for triage.
      return parsed.detail || fallback;
  }
}

/** Map HTTP status codes + structured server body to AppError. */
export function httpToAppError(status: number, body?: string): AppError {
  const parsed = parseServerError(body);
  const ctx = parsed
    ? {
        serverErrorCode: parsed.error,
        field: parsed.field,
        constraint: parsed.constraint,
        requestId: parsed.requestId,
        hint: parsed.hint,
      }
    : undefined;

  switch (status) {
    case 400: {
      const msg = friendlyMessage(parsed, body, 'Invalid request');
      // If we know which field failed, surface it through ValidationError so
      // the UI can highlight it. Otherwise fall back to a generic AppError.
      if (parsed?.field) {
        return new ValidationError(msg, { [parsed.field]: msg }, ctx);
      }
      return new AppError({
        code: 'VALIDATION_ERROR',
        message: msg,
        severity: 'warning',
        context: ctx,
      });
    }
    case 401:
      return new AuthError('Session expired. Please sign in again.', true);
    case 402:
      // B1 (2026-06-12): tier-gate denial. The server's detail already names
      // the required plan + points at Billing; requiredTier rides in context
      // so surfaces can render an upgrade CTA linking to /billing.
      return new AppError({
        code: 'UPGRADE_REQUIRED',
        message: friendlyMessage(
          parsed,
          body,
          'This feature requires a higher plan. You can upgrade any time in Billing.',
        ),
        severity: 'info',
        recoverable: true,
        context: { ...(ctx || {}), requiredTier: parsed?.requiredTier },
      });
    case 403:
      return new AppError({
        code: 'FORBIDDEN',
        message: friendlyMessage(parsed, body, 'You do not have permission for this action'),
        severity: 'critical',
        recoverable: false,
        context: ctx,
      });
    case 404:
      return new AppError({
        code: 'NOT_FOUND',
        message: friendlyMessage(parsed, body, 'Resource not found'),
        severity: 'warning',
        context: ctx,
      });
    case 409:
      return new AppError({
        code: 'VALIDATION_ERROR',
        message: friendlyMessage(parsed, body, 'Conflict with existing data'),
        severity: 'warning',
        context: ctx,
      });
    case 429:
      return new AppError({
        code: 'RATE_LIMITED',
        message: 'Too many requests. Please wait a moment.',
        severity: 'warning',
        recoverable: true,
        context: ctx,
      });
    case 503:
      return new NetworkError(
        friendlyMessage(parsed, body, 'Service temporarily unavailable. Please retry.'),
        false,
      );
    default:
      return new AppError({
        code: 'UNKNOWN',
        message: friendlyMessage(parsed, body, 'An unexpected error occurred'),
        severity: 'critical',
        context: ctx,
      });
  }
}
